/**
 * Le **type de contrôle** — `Inquiry.purpose` ([M2](../../../../docs/MODELE-DONNEES.md#m2),
 * [M32](../../../../docs/MODELE-DONNEES.md#m32)).
 *
 * ⚠️ **Ce n'est pas une énumération, et la colonne est du texte libre.** [M10](#m10) trace la
 * ligne de partage : on contraint les vocabulaires dont notre code dépend (`status`, `result`),
 * on laisse libres ceux du client, imprimés sur un rapport, qui bougeront sans nous prévenir. Le
 * legacy n'en connaissait que quatre là où le rapport validé en liste six — la liste a déjà
 * bougé deux fois (l'enquête, #505). Ce module **suggère**, il ne valide pas.
 */
/**
 * Les valeurs suggérées : les six de la ligne « Type de contrôle » du rapport validé, dans son
 * ordre, puis l'**enquête**, demandée à la recette du 20/10 (#505) :
 *
 * ```
 * Type de contrôle   ☐ SYAM  ☐ Vente  ☐ Systématique  ☐ Pollution  ☐ Rétro  ☐ Avant travaux
 * ```
 *
 * ⚠️ L'enquête n'a pas de case sur cette ligne du rapport ; le classeur « Rapport AC Terrain » de
 * GHP en a une (`ghp-workbook.ts`).
 */
export const INQUIRY_PURPOSES = [
    'syam',
    'sale',
    'systematic',
    'pollution',
    'retro',
    'pre_works',
    'investigation',
];
/**
 * Les libellés **longs**, ceux de la fiche de dossier et du panneau des délais réglementaires
 * (`ghp-bo/editor.js:86` lit `typeMeta[type].label`).
 *
 * C'est la seule liste de libellés qui fasse foi : le panneau des délais de « Relances » la lit
 * plutôt que de recopier ses chaînes, faute de quoi il finirait par nommer un type que le tableau
 * des demandes ne connaît pas.
 *
 * ⚠️ **« Rétrocession », pas le « Rétro » du rapport** : la recette des 13-14/10 (#439) a demandé le
 * mot entier à l'écran. La case du rapport garde son abréviation — c'est un libellé imprimé.
 */
export const INQUIRY_PURPOSE_LABELS = {
    syam: 'SYAM',
    sale: 'Vente',
    systematic: 'Systématique',
    pollution: 'Pollution',
    retro: 'Rétrocession',
    pre_works: 'Avant travaux',
    investigation: 'Enquête',
};
/**
 * Les libellés **de la liste déroulante d'import**, verbatim de `OCR_TYPES`
 * (`ghp-bo/screens.js:254`) : le prototype y réemploie les clés de son jeu de données, d'où le
 * pluriel de « Ventes » et l'abréviation « Systém. ». Seul écart : « Rétrocession » en toutes
 * lettres là où le prototype écrivait « Retro », à la demande de la recette (#439).
 *
 * ⚠️ **Deux longueurs de libellé pour la même notion, et c'est voulu** — même arbitrage que
 * `Sector.name` / `short_name` et que `private_professional` (glossaire) : le prototype affiche
 * la forme courte là où la place manque et la forme longue là où il faut nommer. Les réduire à
 * une seule ferait perdre le 1:1 sur l'un des deux écrans.
 */
export const INQUIRY_PURPOSE_OPTION_LABELS = {
    syam: 'SYAM',
    sale: 'Ventes',
    systematic: 'Systém.',
    pollution: 'Pollution',
    retro: 'Rétrocession',
    pre_works: 'Avant travaux',
    investigation: 'Enquête',
};
/**
 * Le libellé d'un type de contrôle, **avec son repli sur la valeur brute** — exactement ce que
 * fait le prototype (`typeMeta[t].label || t`).
 *
 * ⚠️ C'est ce repli qui rend le champ libre affichable : une valeur ajoutée par GHP en base
 * s'affiche telle quelle au lieu de disparaître de l'écran. Écrit une fois ici plutôt que dans
 * chaque colonne — trois replis divergeraient au premier code inconnu.
 */
export function inquiryPurposeLabel(purpose) {
    if (purpose === null)
        return null;
    return INQUIRY_PURPOSE_LABELS[purpose] ?? purpose;
}
//# sourceMappingURL=purpose.js.map